/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * routeCache
 * ----------
 * Keeps downloaded routes on the device so a refresh — or coming back
 * tomorrow — costs no request. The dwell gate (see routeDwell) stops routes
 * being fetched while scrolling; this stops the same route being fetched
 * twice, ever, on this device.
 *
 * Why IndexedDB and not the HTTP cache: the route request is a POST
 * (`/arcee/application/table/fetch/`), and browsers do not reuse cached
 * responses for POST, so `Cache-Control` cannot help here. localStorage is
 * the wrong tool too — one route is tens of kilobytes and the ~5MB cap would
 * be gone in a handful of activities.
 *
 * What is stored is the DOWNSAMPLED route (what routeFromPoints keeps), not
 * the server's rows: ~800 points of four numbers each instead of up to 15,000
 * rows of twelve columns. Points go in as packed arrays and come back out in
 * the server's row shape, so nothing downstream knows the difference.
 *
 * Everything here is best-effort. Private windows, blocked storage, a full
 * disk and browsers without IndexedDB must all degrade to "no cache" rather
 * than break the feed, so every call resolves rather than rejects.
 *
 * Entries are scoped per user and purged on sign-out: a GPS trace shows where
 * somebody lives, and must not outlive their session on a shared computer.
 */

const DB_NAME = 'athlohive-feed';
const DB_VERSION = 1;
const STORE = 'routes';

/** Bump to invalidate every stored route (e.g. if the packed shape changes). */
// v2: routes keep their highest point through the downsample (Max Elevation).
export const CACHE_VERSION = 'v2';

/**
 * How many routes are kept. Least-recently-used entries are evicted once this
 * is passed. There is no standard for this number — at roughly 28KB a route,
 * 300 is about 8MB, which caches around a year of activity for most athletes
 * while keeping this site's footprint small enough that a browser clearing
 * storage under disk pressure never picks us. A feed page is 20 activities,
 * so 300 is roughly the last fifteen pages.
 *
 * A byte cap used to sit alongside this and never once fired: reaching 50MB
 * would take about 1,800 routes, and the count cap stops that six times over.
 * Dropped rather than kept as dead weight — `bytes` is still recorded on each
 * entry, for inspecting the store.
 * export const MAX_BYTES = 50 * 1024 * 1024;
 */
export const MAX_ROUTES = 300;

/** ~0.11 m of precision: far finer than any map drawn here, and much smaller. */
const COORD_DP = 6;
const ALT_DP = 1;

const round = (n, dp) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const f = 10 ** dp;
  return Math.round(Number(n) * f) / f;
};

// ─── Pure policy (unit-tested without IndexedDB) ────────────────────────────

/** Storage key for one user's copy of one activity's route. */
export function routeCacheKey(userId, activityId) {
  return `${CACHE_VERSION}:${userId}:${activityId}`;
}

/** Route points → compact [seq, lat, lng, alt] tuples. */
export function packPoints(route) {
  if (!Array.isArray(route)) return [];
  return route
    .filter((p) => p && p.lat !== null && p.lat !== undefined && p.lng !== null && p.lng !== undefined)
    .map((p, i) => [
      Number.isFinite(Number(p.seq)) ? Number(p.seq) : i,
      round(p.lat, COORD_DP),
      round(p.lng, COORD_DP),
      round(p.alt, ALT_DP),
    ]);
}

/**
 * Packed tuples → the server's row shape, so routeFromPoints can take them
 * exactly as it takes a fresh response.
 */
export function unpackPoints(packed) {
  if (!Array.isArray(packed)) return [];
  return packed.map(([sequence, latitude, longitude, altitude]) => ({
    sequence, latitude, longitude, altitude,
  }));
}

/** Rough stored size of one packed route — recorded for inspection only. */
export function estimateBytes(packed) {
  return Array.isArray(packed) ? packed.length * 36 : 0;
}

/**
 * Which entries to drop so the store fits the cap, least-recently-used first.
 * `entries` is [{ key, lastUsedAt }] in any order.
 */
export function selectEvictions(entries, { maxRoutes = MAX_ROUTES } = {}) {
  const sorted = [...entries].sort((a, b) => (a.lastUsedAt || 0) - (b.lastUsedAt || 0));
  const excess = sorted.length - maxRoutes;
  return excess > 0 ? sorted.slice(0, excess).map((e) => e.key) : [];
}

// ─── IndexedDB plumbing (best-effort; exercised in the browser tests) ───────

/**
 * Reading `indexedDB` can itself throw, not merely be undefined: Safari's
 * private mode and some privacy extensions install a throwing getter, and
 * `typeof` evaluates it. So this is guarded rather than a plain check.
 */
const available = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (e) {
    return false;
  }
};

let dbPromise = null;

/** Never throws and never rejects: resolves to a database, or to null. */
function openDb() {
  try {
    if (!available()) return Promise.resolve(null);
  } catch (e) {
    return Promise.resolve(null);
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { resolve(null); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('byUser', 'userId', { unique: false });
        store.createIndex('byLastUsed', 'lastUsedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  }).catch(() => null);
  return dbPromise;
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Resolves a request to its result, or to `fallback` on any failure. */
function ask(request, fallback = null) {
  return new Promise((resolve) => {
    try {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(fallback);
    } catch (e) { resolve(fallback); }
  });
}

/**
 * Stored routes for these activities, as a Map of activityId → server-shaped
 * rows. Missing or unreadable entries are simply absent.
 */
export async function readRoutes(userId, activityIds) {
  const out = new Map();
  if (userId === null || userId === undefined || !Array.isArray(activityIds) || !activityIds.length) return out;
  try {
    const db = await openDb();
    if (!db) return out;
    const store = tx(db, 'readonly');
    const found = await Promise.all(
      activityIds.map((id) => ask(store.get(routeCacheKey(userId, id)))),
    );
    const now = Date.now();
    const touch = [];
    found.forEach((rec, i) => {
      if (!rec || !Array.isArray(rec.points)) return;
      out.set(String(activityIds[i]), unpackPoints(rec.points));
      touch.push({ ...rec, lastUsedAt: now });
    });
    // Keep LRU honest; failure here only costs eviction accuracy.
    if (touch.length) {
      try {
        const rw = tx(db, 'readwrite');
        touch.forEach((rec) => rw.put(rec));
      } catch (e) { /* not worth failing a read over */ }
    }
  } catch (e) { /* unreadable store → behave as a miss */ }
  return out;
}

/**
 * Stores one activity's downsampled route, then trims the store back inside
 * its caps. An empty route is deliberately NOT stored: an activity that
 * returned no points may genuinely get them later, and React Query already
 * stops it being asked twice in one session.
 */
export async function writeRoute(userId, activityId, route) {
  if (userId === null || userId === undefined || !activityId) return;
  const points = packPoints(route);
  if (!points.length) return;
  try {
    const db = await openDb();
    if (!db) return;
    const now = Date.now();
    const rec = {
      key: routeCacheKey(userId, activityId),
      userId: String(userId),
      activityId: String(activityId),
      points,
      bytes: estimateBytes(points),
      savedAt: now,
      lastUsedAt: now,
    };
    await ask(tx(db, 'readwrite').put(rec));
    await enforceCaps(db);
  } catch (e) { /* quota or a closed db: the feed carries on uncached */ }
}

/** Drops least-recently-used entries until the store is back within MAX_ROUTES. */
async function enforceCaps(db) {
  try {
    const all = await ask(tx(db, 'readonly').getAll(), []);
    if (!Array.isArray(all) || all.length <= MAX_ROUTES) return;
    const drop = selectEvictions(
      all.map((e) => ({ key: e.key, lastUsedAt: e.lastUsedAt })),
    );
    if (!drop.length) return;
    const store = tx(db, 'readwrite');
    drop.forEach((key) => store.delete(key));
  } catch (e) { /* leave the store as it is */ }
}

/** Removes every route stored for one user — called on sign-out. */
export async function purgeUser(userId) {
  if (userId === null || userId === undefined) return;
  try {
    const db = await openDb();
    if (!db) return;
    const store = tx(db, 'readwrite');
    const index = store.index('byUser');
    const keys = await ask(index.getAllKeys(IDBKeyRange.only(String(userId))), []);
    (keys || []).forEach((key) => store.delete(key));
  } catch (e) { /* best effort */ }
}

/** Empties the whole store (every user) — the belt-and-braces sign-out path. */
export async function clearAllRoutes() {
  try {
    const db = await openDb();
    if (!db) return;
    await ask(tx(db, 'readwrite').clear());
  } catch (e) { /* best effort */ }
}

/** Test seam: forget the cached connection. */
export function resetRouteCacheForTests() {
  dbPromise = null;
}
